/**
 * Singleton QueryClient for the Banyan AI frontend.
 *
 * Architecture: see `memory-bank/creative/TASK-079-phase1-state-management.md`.
 *
 * Production defaults
 * -------------------
 *   - staleTime: 0 (per-hook explicit; no implicit global cache window)
 *   - refetchOnWindowFocus: false (admin tool — refocus shouldn't trigger
 *     a network storm; per-page invalidation is the canonical refresh path)
 *   - mutations.retry: false (mutations should fail loudly, never silently)
 *   - queries.retry: skip on 4xx (apiClient already retries 401 once with a
 *     refreshed token; other 4xx are programmer errors and shouldn't loop)
 *
 * Tests construct their own clients via `tests/utils/createTestQueryClient.js`
 * which disables retry entirely and sets gcTime: Infinity so cache state is
 * deterministic.
 */
import { QueryClient } from '@tanstack/react-query';

const MAX_QUERY_RETRY_COUNT = 2;

interface ErrorWithStatus {
  status?: number;
  sessionExpired?: boolean;
}

function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const e = error as ErrorWithStatus | null | undefined;
  if (e?.status && e.status >= 400 && e.status < 500) {
    return false;
  }
  return failureCount < MAX_QUERY_RETRY_COUNT;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        retry: shouldRetryQuery,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * Singleton client used by the app shell. Constructed once at module load so
 * <QueryClientProvider client={queryClient}> in `frontend/src/main.jsx`
 * retains cache state across hot-module-reload boundaries.
 */
export const queryClient: QueryClient = createQueryClient();
